import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CandidateForm, ClassifyDetail, PageAssignment } from "../api/client";
import { ConfirmModal } from "@/components/ConfirmModal";
import styles from "./ClassifyResolver.module.css";

/**
 * CS-2 Phase 7a — ClassifyResolver
 *
 * Unified cascading resolver for the Classification Station (CS-2). Combines
 * the form picker and page-type picker into a single component:
 *
 * 1. **Type section** — always rendered. F-087 — the job-grouped form picker
 *    is a COLLAPSED combobox (ARIA select-only pattern): a trigger showing the
 *    current selection opens an on-demand popup listbox (`groups`, own job
 *    pinned first — F-076), ★ on the suggested form, declared-form conflict
 *    hint, a context strip for the selected form, and a cross-job confirm
 *    dialog gate (F-064) before routing out of the batch's job type. Calls
 *    `props.onClassify(formId)` when the operator confirms (immediately for
 *    an own-job form; after Continue for a cross-job route).
 *
 * 2. **Pages section** — disabled (aria-disabled="true") when `detail.form` is
 *    null. When a form is resolved:
 *    - Single page type → read-only label (F-056 fix, no <select>).
 *    - Multiple page types → <select> of page types + optional Attachment.
 *    Calls `props.onAssign(pageId, assignment)` on change.
 *
 * PRESENTATIONAL — no API calls. All async work happens in the parent; this
 * component calls the passed-in handlers.
 */

export interface ClassifyResolverProps {
  detail: ClassifyDetail;
  onClassify: (formId: string) => void;
  onAssign: (pageId: string, assignment: PageAssignment) => void;
  /** Task 11 — fires whenever the operator's candidate-form selection changes
   *  (including the initial pre-selected suggestion), so the parent can look
   *  up layout-variant eligibility for whichever form is currently picked.
   *  `null` when nothing is selected yet. PRESENTATIONAL component stays
   *  API-call-free — the parent does the fetch. */
  onSelectedFormChange?: (form: CandidateForm | null) => void;
}

export function ClassifyResolver({
  detail,
  onClassify,
  onAssign,
  onSelectedFormChange,
}: ClassifyResolverProps) {
  const [selectedFormId, setSelectedFormId] = useState<string>(
    // Pre-select the fingerprint's suggested form (mirrors ClassifyDetailPanel logic).
    detail.suggestion?.formId ?? detail.declaredFormSuggestion?.suggestedFormId ?? "",
  );
  // F-064 — cross-job routes need an explicit confirm gate before firing.
  const [confirmingRoute, setConfirmingRoute] = useState(false);
  // F-087 — the job-grouped picker is collapsed until the operator opens it.
  const [pickerOpen, setPickerOpen] = useState(false);
  // E-028 — the popup is rendered in a portal with fixed positioning so it
  // escapes the `overflow: hidden` ancestors in ClassifyDetailPanel (which
  // previously clipped it and blocked sizing the list to its content). These
  // coords, measured against the trigger on open and kept fresh on
  // scroll/resize, anchor the fixed popup; `direction` flips it above the
  // trigger when there isn't room below so it never runs off the viewport.
  const [popupCoords, setPopupCoords] = useState<{
    left: number;
    top?: number;
    bottom?: number;
    minWidth: number;
    direction: "up" | "down";
  } | null>(null);

  // ── Derived: group candidate forms by owning job type, own job pinned first ──
  // A null jobTypeName (legacy own-type-only candidates) falls under "This job type".
  const groups = Object.entries(
    detail.candidateForms.reduce<Record<string, typeof detail.candidateForms>>(
      (acc, f) => {
        const k = f.jobTypeName ?? "This job type";
        (acc[k] ??= []).push(f);
        return acc;
      },
      {},
    ),
  ).sort(([, a], [, b]) => {
    const aOwn = a[0]!.jobTypeId === detail.jobTypeId ? 0 : 1;
    const bOwn = b[0]!.jobTypeId === detail.jobTypeId ? 0 : 1;
    return aOwn - bOwn;
  });

  const selected = detail.candidateForms.find((f) => f.id === selectedFormId) ?? null;

  // Task 11 — tell the parent whenever the candidate-form selection changes
  // (including the initial pre-selected suggestion) so it can look up
  // layout-variant eligibility for the form currently on screen.
  useEffect(() => {
    onSelectedFormChange?.(selected);
    // `selected` is derived from selectedFormId + detail.candidateForms; keying
    // off selectedFormId alone is enough and avoids re-firing on unrelated
    // detail refetches that leave the id unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFormId]);

  const isCrossJob =
    selected != null && detail.jobTypeId != null && selected.jobTypeId !== detail.jobTypeId;

  // Shared by the collapsed trigger, the popup options, and the context strip —
  // one place for the "Job → Form" format and the ★-suggested rule.
  const formLabel = (f: { jobTypeName: string | null; name: string }) =>
    f.jobTypeName ? `${f.jobTypeName} → ${f.name}` : f.name;
  const isSuggested = (formId: string) =>
    detail.suggestion?.formId === formId ||
    detail.declaredFormSuggestion?.suggestedFormId === formId;

  // ── Roving tabindex (ARIA listbox pattern) inside the F-087 popup ──────────
  // Flatten the grouped forms into visual order so Arrow/Home/End can walk
  // across group boundaries. Exactly one option is tabbable at a time: the
  // resolved selection, or the first option when nothing is selected/resolved.
  const flatForms = groups.flatMap(([, forms]) => forms);
  const tabbableId = selected?.id ?? flatForms[0]?.id;
  const optionRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  // E-028 — the popup is portaled out of `pickerRef`, so click-outside must
  // also exempt it (else a mousedown on an option closes the picker before the
  // option's click fires and the selection is lost).
  const listboxRef = useRef<HTMLUListElement | null>(null);

  const focusOption = (formId: string | undefined) => {
    if (formId == null) return;
    optionRefs.current[formId]?.focus();
  };

  // E-028 — the popup's max height (mirrors --formList's max-height: 40vh in
  // the CSS module); used purely to estimate whether it fits below the
  // trigger. The real clamp still comes from CSS — this only decides which
  // side to anchor to.
  const ESTIMATED_LIST_HEIGHT = 320;
  // Small breathing gap between the trigger and the popup (matches the old
  // CSS `calc(100% + var(--space-1))` offset, now that positioning is inline).
  const POPUP_GAP = 4;

  // E-028 — measure the trigger and derive the fixed-position coords for the
  // portaled popup: anchor below by default, flip above when there's no room.
  // Shared by open and by the scroll/resize re-anchor so the popup tracks the
  // trigger while open.
  const measurePopup = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < ESTIMATED_LIST_HEIGHT && spaceAbove > spaceBelow;
    setPopupCoords({
      left: rect.left,
      minWidth: rect.width,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + POPUP_GAP, direction: "up" as const }
        : { top: rect.bottom + POPUP_GAP, direction: "down" as const }),
    });
  };

  // F-087 — on open, move focus into the popup (selected-or-first option).
  // E-028 — also measure the trigger so the portaled popup anchors correctly
  // and flips above when there's no room below. useLayoutEffect so the measure
  // (and the resulting position) commits before paint — no flash at (0,0) — and
  // so the option nodes are already mounted (the portal renders on pickerOpen
  // alone) when focusOption runs.
  useLayoutEffect(() => {
    if (!pickerOpen) {
      setPopupCoords(null);
      return;
    }
    measurePopup();
    focusOption(tabbableId);
    // Fixed positioning doesn't follow the trigger on scroll — re-anchor.
    const reanchor = () => measurePopup();
    window.addEventListener("scroll", reanchor, true);
    window.addEventListener("resize", reanchor);
    return () => {
      window.removeEventListener("scroll", reanchor, true);
      window.removeEventListener("resize", reanchor);
    };
    // tabbableId intentionally read once per open — focus must not jump while browsing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen]);

  // F-087 — clicking outside the picker closes the popup.
  useEffect(() => {
    if (!pickerOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // Exempt both the trigger (pickerRef) and the portaled popup (listboxRef).
      if (
        !pickerRef.current?.contains(target) &&
        !listboxRef.current?.contains(target)
      ) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [pickerOpen]);

  const closePicker = () => {
    setPickerOpen(false);
    triggerRef.current?.focus();
  };

  const selectAndClose = (formId: string) => {
    setSelectedFormId(formId);
    closePicker();
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    // Enter/Space fire the native button click (toggle); ArrowDown/Up open.
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setPickerOpen(true);
    } else if (e.key === "Escape" && pickerOpen) {
      e.preventDefault();
      closePicker(); // focus is already on the trigger; keeps one Escape path
    }
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, formId: string) => {
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        selectAndClose(formId);
        return;
      case "Escape":
        e.preventDefault();
        closePicker();
        return;
      case "ArrowDown": {
        e.preventDefault();
        const idx = flatForms.findIndex((f) => f.id === formId);
        focusOption(flatForms[idx + 1]?.id);
        return;
      }
      case "ArrowUp": {
        e.preventDefault();
        const idx = flatForms.findIndex((f) => f.id === formId);
        focusOption(flatForms[idx - 1]?.id);
        return;
      }
      case "Home":
        e.preventDefault();
        focusOption(flatForms[0]?.id);
        return;
      case "End":
        e.preventDefault();
        focusOption(flatForms[flatForms.length - 1]?.id);
        return;
      case "Tab":
        // Let focus move on naturally, but don't leave a headless popup open.
        setPickerOpen(false);
        return;
      default:
        return;
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleConfirm = () => {
    // Gate on `selected`, not `selectedFormId` — a stale pre-selected suggestion
    // (id absent from candidateForms) is a non-empty string that resolves to no
    // list entry; firing it at the API would classify with a dead form id.
    if (!selected) return;
    if (isCrossJob) {
      setConfirmingRoute(true); // F-064 — dialog gate before routing out of the batch's job
      return;
    }
    onClassify(selected.id);
  };

  const handlePageTypeChange = (pageId: string, value: string) => {
    const assignment: PageAssignment =
      value === "__attachment__"
        ? { kind: "attachment" }
        : { kind: "form_page", pageTypeId: value };
    onAssign(pageId, assignment);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const pagesDisabled = detail.form == null;
  // Hoisted: doesn't depend on the page — safe because detail.form is checked by pagesDisabled.
  const singleType = detail.form != null && detail.form.pageTypes.length === 1;

  return (
    <div className={styles.resolver}>
      {/* ── Type section — always rendered ─────────────────────────────────── */}
      <div className={styles.typeSection}>
        <p className={styles.sectionHeading}>Type</p>

        {/* Declared-form conflict banner (R-267 6d) */}
        {detail.declaredFormSuggestion && (
          <div className={styles.conflictBanner} role="status">
            <strong>Declared form may be wrong.</strong>{" "}
            Declared: {detail.declaredFormSuggestion.declaredFormName ?? "—"} ·
            Fingerprint suggests: {detail.declaredFormSuggestion.suggestedFormName ?? "—"}
            {detail.declaredFormSuggestion.confidence != null
              ? ` (${Math.round(detail.declaredFormSuggestion.confidence * 100)}%)`
              : ""}
          </div>
        )}

        {/* F-076/F-087 — job-grouped form picker (own job pinned first) as a
            COLLAPSED combobox: the trigger shows the current selection; the
            grouped listbox renders on demand so the detail pane's document
            view keeps its vertical space. */}
        <div className={styles.picker} ref={pickerRef}>
          <button
            type="button"
            ref={triggerRef}
            role="combobox"
            className={styles.pickerTrigger}
            aria-haspopup="listbox"
            aria-expanded={pickerOpen}
            aria-controls="cs-form-picker-listbox"
            aria-label="Select form type"
            data-testid="cs-form-picker-trigger"
            onClick={() => setPickerOpen((o) => !o)}
            onKeyDown={handleTriggerKeyDown}
          >
            <span className={selected ? undefined : styles.pickerPlaceholder}>
              {selected
                ? formLabel(selected) + (isSuggested(selected.id) ? " ★ suggested" : "")
                : "Select form type…"}
            </span>
            <span className={styles.pickerChevron} aria-hidden="true">
              {pickerOpen ? "▲" : "▼"}
            </span>
          </button>

          {pickerOpen &&
            createPortal(
            <ul
              ref={listboxRef}
              id="cs-form-picker-listbox"
              className={styles.formList}
              role="listbox"
              aria-label="Select form type"
              // E-028 — the popup is portaled to <body> and fixed-positioned so
              // it escapes ClassifyDetailPanel's `overflow: hidden` clipping and
              // can size to content. `data-direction` still records which side
              // it anchored to (tests + any directional styling depend on it).
              // Rendered on pickerOpen alone (not gated on coords) so the option
              // nodes exist when focus moves in; hidden until measured to avoid
              // a one-frame flash at the wrong position.
              data-direction={popupCoords?.direction ?? "down"}
              style={{
                position: "fixed",
                left: popupCoords?.left,
                top: popupCoords?.top,
                bottom: popupCoords?.bottom,
                minWidth: popupCoords?.minWidth,
                visibility: popupCoords ? "visible" : "hidden",
              }}
            >
              {groups.map(([group, forms], groupIndex) => {
                const groupLabelId = `cs-form-group-label-${groupIndex}`;
                return (
                  <li key={group} className={styles.formGroup} role="presentation">
                    <p id={groupLabelId} className={styles.formGroupLabel}>
                      {group}
                    </p>
                    <ul
                      className={styles.formGroupItems}
                      role="group"
                      aria-labelledby={groupLabelId}
                    >
                      {forms.map((f) => {
                        return (
                          <li
                            key={f.id}
                            ref={(el) => {
                              optionRefs.current[f.id] = el;
                            }}
                            role="option"
                            aria-selected={selectedFormId === f.id}
                            tabIndex={tabbableId === f.id ? 0 : -1}
                            className={
                              selectedFormId === f.id ? styles.formItemSelected : styles.formItem
                            }
                            // E-028 — the item itself truncates with an
                            // ellipsis (CSS); the full "Job → Form" label is
                            // still available on hover/focus via title.
                            title={formLabel(f) + (isSuggested(f.id) ? " ★ suggested" : "")}
                            onClick={() => selectAndClose(f.id)}
                            onKeyDown={(e) => handleOptionKeyDown(e, f.id)}
                          >
                            {formLabel(f)}
                            {isSuggested(f.id) ? " ★ suggested" : ""}
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>,
            document.body,
          )}
        </div>

        {selected && (
          <div className={styles.formContext} data-testid="cs-form-context">
            <strong>{formLabel(selected)}</strong>
            {isCrossJob && (
              <span className={styles.crossJobHint}>
                {" "}
                · different job type — confirming will route this document
              </span>
            )}
          </div>
        )}

        <button
          type="button"
          className={styles.btn}
          disabled={!selected}
          onClick={handleConfirm}
        >
          Confirm
        </button>

        {confirmingRoute && selected && (
          <ConfirmModal
            open
            title="Route document to a different job?"
            description={`This will route the document out of ${
              detail.candidateForms.find((f) => f.jobTypeId === detail.jobTypeId)?.jobTypeName ??
              "this job"
            } to ${selected.jobTypeName ?? "the selected job"} in a new batch. Continue?`}
            confirmLabel="Continue"
            onConfirm={() => {
              setConfirmingRoute(false);
              onClassify(selected.id);
            }}
            onCancel={() => setConfirmingRoute(false)}
          />
        )}
      </div>

      {/* ── Pages section — disabled until form resolved ───────────────────── */}
      <div
        className={styles.pagesSection}
        data-testid="cs-pages-section"
        aria-disabled={pagesDisabled ? "true" : "false"}
      >
        <p className={styles.sectionHeading}>Pages</p>

        {pagesDisabled ? (
          <p className={styles.disabledHint}>Resolve the type first</p>
        ) : (
          detail.pages.map((page) => (
            <div key={page.id} className={styles.pageRow}>
              <span className={styles.pageLabel}>p{page.pageNumber}</span>

              {singleType ? (
                // F-056 fix: read-only label when there is only one page type
                <span className={styles.pageTypeReadOnly}>
                  {detail.form!.pageTypes[0]!.name}
                </span>
              ) : (
                // Multiple page types → <select>
                <select
                  className={styles.formSelect}
                  aria-label="Assign page type"
                  defaultValue={page.pageTypeId ?? ""}
                  onChange={(e) => handlePageTypeChange(page.id, e.target.value)}
                >
                  <option value="">Page type…</option>
                  {detail.form!.pageTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                  {detail.form!.allowsAttachments && (
                    <option value="__attachment__">Attachment</option>
                  )}
                </select>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
